import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Icon, Modal } from '@/components/ui';
import { THEMES, THEME_LABELS, useThemeStore, type Theme } from '@/stores/themeStore';
import { FONT_FAMILIES, FONT_FAMILY_LABELS, useFontFamilyStore, type FontFamily } from '@/stores/fontFamilyStore';
import { DENSITIES, DENSITY_LABELS, useDensityStore, type Density } from '@/stores/densityStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

type TabKey = 'apparence' | 'general' | 'systemes';

export interface PreferencesModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Platform preferences. Strictly about how the dashboard looks and behaves
 * for this operator — never mixes with test configuration (which lives at
 * /config in the sticky nav).
 */
export function PreferencesModal({ open, onClose }: PreferencesModalProps) {
  const [tab, setTab] = useState<TabKey>('apparence');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Icon name="sliders" size={16} />
          Paramètres de la plateforme
        </span>
      }
      subtitle="Apparence · Général · Systèmes"
      size="lg"
    >
        <nav role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', padding: '0 12px', flexShrink: 0 }}>
          {(['apparence', 'general', 'systemes'] as TabKey[]).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              style={{
                background: 'none', border: 'none',
                padding: '11px 14px',
                fontSize: 11, fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: tab === k ? 'var(--accent)' : 'var(--text-muted)',
                borderBottom: `2px solid ${tab === k ? 'var(--accent)' : 'transparent'}`,
                cursor: 'pointer',
              }}
            >
              {({ apparence: 'Apparence', general: 'Général', systemes: 'Systèmes' } as const)[k]}
            </button>
          ))}
        </nav>

        <div style={{ overflowY: 'auto', padding: 20, flex: '1 1 auto', minHeight: 0 }}>
          {tab === 'apparence' && <ApparencePanel />}
          {tab === 'general'   && <GeneralPanel />}
          {tab === 'systemes'  && <SystemesPanel />}
        </div>

        <footer style={{
          padding: '10px 20px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
          flexShrink: 0,
        }}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
            Les préférences sont enregistrées dans ce navigateur
          </span>
          <Button variant="ghost" size="sm" onClick={onClose}>Fermer</Button>
        </footer>
    </Modal>
  );
}

/* ─── Apparence ─────────────────────────────────────────────────────── */

function ApparencePanel() {
  const { theme, setTheme } = useThemeStore();
  const { fontFamily, setFontFamily } = useFontFamilyStore();
  const { density, setDensity } = useDensityStore();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <section>
        <SectionLabel>Thème</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {THEMES.map((t) => (
            <ThemeTile key={t} value={t} active={theme === t} onSelect={() => setTheme(t)} />
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Famille typographique</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {FONT_FAMILIES.map((f) => (
            <FontTile key={f} value={f} active={fontFamily === f} onSelect={() => setFontFamily(f)} />
          ))}
        </div>
      </section>

      <section>
        <SectionLabel>Densité</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {DENSITIES.map((d) => (
            <DensityTile key={d} value={d} active={density === d} onSelect={() => setDensity(d)} />
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="label" style={{ marginBottom: 10 }}>{children}</div>
  );
}

/* ─── Theme tile — 3 vertical frozen swatches (surface / text / accent) ─ */

function ThemeTile({ value, active, onSelect }: { value: Theme; active: boolean; onSelect: () => void }) {
  const info = THEME_LABELS[value];
  // Swatches use the theme's *absolute* colours so they never change when
  // the active theme changes — they remain identity markers.
  const sw = info.swatches;
  return (
    <button
      onClick={onSelect}
      aria-pressed={active}
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--surface-1)',
        boxShadow: active ? 'inset 2px 0 0 var(--accent)' : 'none',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {/* Frozen swatches — absolute OKLCH values that never re-skin. */}
      <div
        aria-hidden
        style={{
          display: 'flex', flexDirection: 'column',
          gap: 3,
          padding: 3,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          background: sw.surface,
          flexShrink: 0,
        }}
      >
        <span style={{
          width: 22, height: 10, borderRadius: 2,
          background: sw.surface,
          border: '1px solid rgba(127,127,127,0.25)',
        }} />
        <span style={{
          width: 22, height: 10, borderRadius: 2,
          background: sw.text,
        }} />
        <span style={{
          width: 22, height: 10, borderRadius: 2,
          background: sw.accent,
        }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{info.name}</span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.05em' }}>
          {info.mode === 'dark' ? 'sombre' : 'clair'} · {info.hint}
        </span>
      </div>
    </button>
  );
}

/* ─── Font tile — compact, 2 per row ─────────────────────────────────── */

function FontTile({ value, active, onSelect }: { value: FontFamily; active: boolean; onSelect: () => void }) {
  const info = FONT_FAMILY_LABELS[value];
  return (
    <button
      onClick={onSelect}
      aria-pressed={active}
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--surface-1)',
        boxShadow: active ? 'inset 2px 0 0 var(--accent)' : 'none',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontFamily: info.previewSans, fontSize: 13, fontWeight: 600 }}>{info.name}</span>
        <span style={{
          fontFamily: info.previewMono,
          fontSize: 10,
          color: 'var(--text-muted)',
          marginLeft: 'auto',
        }}>
          0O·1lI
        </span>
      </div>
      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{info.hint}</span>
    </button>
  );
}

/* ─── Density tile — compact, aligned across the 3 options ─────────── */

function DensityTile({ value, active, onSelect }: { value: Density; active: boolean; onSelect: () => void }) {
  const info = DENSITY_LABELS[value];
  const gap = value === 'compact' ? 2 : value === 'comfortable' ? 5 : 8;

  return (
    <button
      onClick={onSelect}
      aria-pressed={active}
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--surface-1)',
        boxShadow: active ? 'inset 2px 0 0 var(--accent)' : 'none',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10,
      }}
    >
      {/* Mini preview — fixed height (28 px) so all 3 tiles line up exactly */}
      <div
        aria-hidden
        style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          gap,
          height: 28,
          width: 32,
          flexShrink: 0,
        }}
      >
        <span style={{ height: 3, background: 'var(--text-muted)', borderRadius: 2, opacity: 0.7, width: '100%' }} />
        <span style={{ height: 3, background: 'var(--text-muted)', borderRadius: 2, opacity: 0.7, width: '70%' }} />
        <span style={{ height: 3, background: 'var(--text-muted)', borderRadius: 2, opacity: 0.7, width: '85%' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.15 }}>{info.name}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.2 }}>{info.hint}</span>
      </div>
    </button>
  );
}

/* ─── Général panel ──────────────────────────────────────────────────── */

function GeneralPanel() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section>
        <SectionLabel>Redirection automatique</SectionLabel>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
          <input type="checkbox" defaultChecked style={{ accentColor: 'var(--accent)' }} />
          Rediriger vers les résultats à la fin d'une campagne (4 s)
        </label>
      </section>
      <section>
        <SectionLabel>Lignes par page (historique)</SectionLabel>
        <select className="input mono" defaultValue="100" style={{ width: 160 }}>
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="200">200</option>
          <option value="500">500</option>
        </select>
      </section>
      <section>
        <SectionLabel>Format de date</SectionLabel>
        <select className="input" defaultValue="fr" style={{ width: 240 }}>
          <option value="fr">Français (16 avril 2026)</option>
          <option value="iso">ISO (2026-04-16)</option>
        </select>
      </section>
    </div>
  );
}

/* ─── Systèmes panel ─────────────────────────────────────────────────── */

function SystemesPanel() {
  const { data: systems = [], isLoading, error } = useQuery({
    queryKey: ['systems'],
    queryFn: () => api.systems(),
    staleTime: 60_000,
  });

  if (isLoading) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</p>;
  if (error)     return <p style={{ color: 'var(--danger)', fontSize: 13 }}>Erreur de chargement.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
        Registre des systèmes Lab enregistrés sur cette plateforme.
        Édition via <code className="ds-code">config/systems_registry.yaml</code>.
      </p>
      {systems.length === 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aucun système enregistré.</p>
      )}
      {systems.map((s) => (
        <div
          key={s.id}
          style={{
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '10px 14px',
            background: 'var(--surface-0)',
            display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <Icon name="server" size={18} />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <span className="mono" style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
              {s.id}
            </span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {s.host}{s.ssh_port ? ` · port ${s.ssh_port}` : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Satisfy the compiler for the `cn` import we might reuse later.
void cn;
